import {useState} from 'react'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {Modal, Button, Table, Form, Badge, Alert} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {GET_ALL_TAGS} from '../../../gql/queries/tagsQuery'
import {ADD_EDIT_TAG, DELETE_TAG_INSURANCE} from '../../../gql/mutations/tagMutations'
import {IResTags, IResAddTag, IAllTagNode} from '../../../types/tags'
import {useGetUser} from '../../../store/userStore'
import {parseId} from '../../../helpers'

const getContrastColor = (hex: string): string => {
  const clean = (hex || '').replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

interface LeadTagManagerProps {
  show: boolean
  onHide: () => void
  onTagsUpdated?: () => void
}

const requiredMark = '*'

const tM = defineMessages({
  title: {id: 'leads.tags.title', defaultMessage: 'Manage tags'},
  name: {id: 'leads.tags.name', defaultMessage: 'Tag name'},
  namePlaceholder: {id: 'leads.tags.namePlaceholder', defaultMessage: 'e.g., Hot Lead, Follow Up'},
  color: {id: 'common.field.color', defaultMessage: 'Color'},
  create: {id: 'leads.tags.create', defaultMessage: 'Create tag'},
  update: {id: 'leads.tags.update', defaultMessage: 'Update tag'},
  existing: {id: 'leads.tags.existing', defaultMessage: 'Existing tags ({count})'},
  empty: {id: 'leads.tags.empty', defaultMessage: 'No tags created yet. Create your first tag above.'},
  scope: {id: 'leads.tags.scope', defaultMessage: 'Scope'},
  global: {id: 'leads.tags.global', defaultMessage: 'Global'},
  editTooltip: {id: 'leads.tags.editTooltip', defaultMessage: 'Edit tag'},
  deleteTooltip: {id: 'leads.tags.deleteTooltip', defaultMessage: 'Delete tag'},
  noPermEdit: {id: 'leads.tags.noPermEdit', defaultMessage: "You don't have permission to edit this tag"},
  noPermDelete: {id: 'leads.tags.noPermDelete', defaultMessage: "You don't have permission to delete this tag"},
  confirmDelete: {id: 'leads.tags.confirmDelete', defaultMessage: 'Are you sure you want to delete this tag?'},
  toastCreated: {id: 'leads.tags.toast.created', defaultMessage: 'Tag created successfully'},
  toastUpdated: {id: 'leads.tags.toast.updated', defaultMessage: 'Tag updated successfully'},
  toastDeleted: {id: 'leads.tags.toast.deleted', defaultMessage: 'Tag deleted successfully'},
  toastSaveFailed: {id: 'leads.tags.toast.saveFailed', defaultMessage: 'Failed to save tag'},
  toastDeleteFailed: {id: 'leads.tags.toast.deleteFailed', defaultMessage: 'Failed to delete tag'},
})

export const LeadTagManager: React.FC<LeadTagManagerProps> = ({show, onHide, onTagsUpdated}) => {
  const intl = useIntl()
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const currentOrgId = useGetUser((s) => s.user?.user?.organization?.id)

  const [editingTag, setEditingTag] = useState<IAllTagNode | null>(null)
  const [formData, setFormData] = useState({name: '', color: '#3699ff'})

  const {data, loading, refetch} = useQuery<IResTags>(GET_ALL_TAGS, {
    skip: !show,
    fetchPolicy: 'no-cache',
  })

  const invalidateTagsCache = (cache: import('@apollo/client').ApolloCache<any>) => {
    cache.evict({fieldName: 'allTagInsurances'})
    cache.gc()
  }

  const [gqlAddEditTag, {loading: saving}] = useMutation<IResAddTag>(ADD_EDIT_TAG, {
    update: (cache) => invalidateTagsCache(cache),
  })
  const [gqlDeleteTag] = useMutation(DELETE_TAG_INSURANCE, {
    update: (cache) => invalidateTagsCache(cache),
  })

  const tags = data?.allTagInsurances?.edges.map(({node}) => node) || []

  const canManage = (tag: IAllTagNode) => {
    if (isSuperAdmin) return true
    if (!tag.organization) return false
    return String(parseId(tag.organization.id)) === String(parseId(currentOrgId || ''))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const result = await gqlAddEditTag({
        variables: {
          input: {
            id: editingTag ? parseId(editingTag.id) : undefined,
            name: formData.name,
            color: formData.color,
          },
        },
      })

      if (result.data?.tagInsuranceCreateupdateMutation?.tagInsurance?.id) {
        toast.success(intl.formatMessage(editingTag ? tM.toastUpdated : tM.toastCreated))
        setFormData({name: '', color: '#3699ff'})
        setEditingTag(null)
        refetch()
        onTagsUpdated?.()
      } else if (result.data?.tagInsuranceCreateupdateMutation?.errors?.length) {
        toast.error(result.data.tagInsuranceCreateupdateMutation.errors[0]?.messages?.[0] || intl.formatMessage(tM.toastSaveFailed))
      }
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(tM.toastSaveFailed))
    }
  }

  const handleEdit = (tag: IAllTagNode) => {
    setEditingTag(tag)
    setFormData({name: tag.name, color: tag.color})
  }

  const handleDelete = async (tag: IAllTagNode) => {
    if (!window.confirm(intl.formatMessage(tM.confirmDelete))) return

    try {
      await gqlDeleteTag({variables: {id: tag.id}})
      toast.success(intl.formatMessage(tM.toastDeleted))
      refetch()
      onTagsUpdated?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(tM.toastDeleteFailed))
    }
  }

  const handleCancel = () => {
    setEditingTag(null)
    setFormData({name: '', color: '#3699ff'})
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(tM.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='tag-form mb-4'>
          <Form onSubmit={handleSubmit}>
            <div className='d-flex align-items-end gap-2 flex-wrap'>
              <Form.Group style={{flex: '2 1 220px'}}>
                <Form.Label className='mb-1 small'>{intl.formatMessage(tM.name)} {requiredMark}</Form.Label>
                <Form.Control
                  type='text'
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={intl.formatMessage(tM.namePlaceholder)}
                />
              </Form.Group>
              <Form.Group style={{flex: '0 0 auto'}}>
                <Form.Label className='mb-1 small'>{intl.formatMessage(tM.color)}</Form.Label>
                <Form.Control
                  type='color'
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  style={{width: '44px', height: '38px', padding: '2px'}}
                  title={formData.color}
                />
              </Form.Group>
              <div className='d-flex gap-2'>
                <Button type='submit' variant='primary' disabled={saving}>
                  {intl.formatMessage(editingTag ? tM.update : tM.create)}
                </Button>
                {editingTag && (
                  <Button type='button' variant='secondary' onClick={handleCancel}>
                    <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </div>

        <div className='tags-list'>
          <h6 className='mb-3'>{intl.formatMessage(tM.existing, {count: tags.length})}</h6>
          {loading ? (
            <div className='text-center py-4'>
              <div className='spinner-border spinner-border-sm' />
            </div>
          ) : tags.length === 0 ? (
            <Alert variant='info'>{intl.formatMessage(tM.empty)}</Alert>
          ) : (
            <Table striped hover size='sm'>
              <thead>
                <tr>
                  <th style={{width: '40px'}}>{intl.formatMessage(tM.color)}</th>
                  <th><FormattedMessage id='common.field.name' defaultMessage='Name' /></th>
                  {isSuperAdmin && <th>{intl.formatMessage(tM.scope)}</th>}
                  <th style={{width: '120px'}}><FormattedMessage id='common.field.actions' defaultMessage='Actions' /></th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => {
                  const manageable = canManage(tag)
                  return (
                    <tr key={tag.id}>
                      <td>
                        <span
                          className='badge rounded-pill px-2'
                          style={{backgroundColor: tag.color, color: getContrastColor(tag.color)}}
                        >
                          {tag.name}
                        </span>
                      </td>
                      <td>
                        <strong>{tag.name}</strong>
                      </td>
                      {isSuperAdmin && (
                        <td>
                          {tag.organization ? (
                            <Badge bg='secondary' text='light'>
                              {tag.organization.name}
                            </Badge>
                          ) : (
                            <Badge bg='dark' text='light' className='border border-secondary'>
                              {intl.formatMessage(tM.global)}
                            </Badge>
                          )}
                        </td>
                      )}
                      <td>
                        <div className='d-flex gap-1'>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            disabled={!manageable}
                            title={manageable ? intl.formatMessage(tM.editTooltip) : intl.formatMessage(tM.noPermEdit)}
                            onClick={() => handleEdit(tag)}
                          >
                            <FormattedMessage id='common.action.edit' defaultMessage='Edit' />
                          </Button>
                          <Button
                            size='sm'
                            variant='outline-danger'
                            disabled={!manageable}
                            title={manageable ? intl.formatMessage(tM.deleteTooltip) : intl.formatMessage(tM.noPermDelete)}
                            onClick={() => handleDelete(tag)}
                          >
                            <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
